import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import './estilodashuser.css';

const TIERS = {
  starter: { label: 'Starter', licencasPorProduto: 2 },
  trader: { label: 'Trader', licencasPorProduto: 5 },
  pro: { label: 'Pro', licencasPorProduto: 10 },
};

function getTipoAssinatura(assinatura) {
  return String(assinatura?.tipo || '').toLowerCase() === 'pacote'
    ? 'pacote'
    : 'individual';
}

function buildWhatsAppPlanLink(message) {
  return `https://wa.me/5542984241307?text=${encodeURIComponent(message)}`;
}

export default function Cliente({ userId }) {
  const { t, i18n } = useTranslation();
  const [cliente, setCliente] = useState(null);
  const [assinaturas, setAssinaturas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [alterandoPlanoId, setAlterandoPlanoId] = useState('');
  const [novosTiers, setNovosTiers] = useState({});
  const locale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'pt-BR';
  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(Number(value || 0));

  useEffect(() => {
    if (userId) carregarDados();
  }, [userId]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [respCliente, respAss, respProd, respPac] = await Promise.all([
        api.get(`/api/clientes/${userId}`),
        api.get('/api/minhas-assinaturas'),
        api.get('/api/produtos-ativos'),
        api.get('/api/pacotes-ativos'),
      ]);

      setCliente(respCliente.data || null);
      setAssinaturas(respAss.data || []);
      setProdutos(respProd.data || []);
      setPacotes(respPac.data || []);
      setMensagem('');
      setTipoMensagem('');
    } catch (error) {
      console.error('Erro ao carregar informações do cliente:', error);
      setMensagem(
        t('dashboard.account.loadError')
      );
      setTipoMensagem('erro');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (campo, valor) => {
    setCliente((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleSalvar = async () => {
    if (!cliente) return;

    try {
      setSalvando(true);
      setMensagem('');
      setTipoMensagem('');

      await api.put(`/api/clientes/${cliente.id}`, {
        nome: cliente.nome,
        email: cliente.email,
        phone: cliente.phone,
        cpf: cliente.cpf,
        slots_contratados: cliente.slots_contratados,
        dia_vencimento: cliente.dia_vencimento,
      });

      setMensagem(t('dashboard.account.updateSuccess'));
      setTipoMensagem('sucesso');
    } catch (error) {
      console.error('Erro ao atualizar dados do cliente:', error);
      const msg =
        error.response?.status === 403
          ? t('dashboard.account.updateForbidden')
          : i18n.resolvedLanguage === 'en'
            ? t('dashboard.account.updateError')
            : error.response?.data?.message || t('dashboard.account.updateError');
      setMensagem(msg);
      setTipoMensagem('erro');
    } finally {
      setSalvando(false);
    }
  };

  const assinaturasAtivas = useMemo(() => {
    return assinaturas.filter((a) => Number(a.status) === 1);
  }, [assinaturas]);

  const resumoAssinaturas = useMemo(() => {
    return assinaturasAtivas.map((a) => {
      const tier = String(a.tier || 'starter');
      const tipo = getTipoAssinatura(a);
      const nomePlano = a.nome_plano || a.codigo_plano || t('dashboard.account.subscription');
      let valor = 0;

      if (tipo === 'pacote') {
        const pacote = pacotes.find(
          (p) => String(p.codigo || '').toLowerCase() === String(a.codigo_plano || '').toLowerCase()
        );
        valor = Number(pacote?.precos?.[tier]?.valor_brl || 0);
      } else {
        const produto = produtos.find(
          (p) => String(p.codigo || '').toLowerCase() === String(a.codigo_plano || '').toLowerCase()
        );
        valor = Number(produto?.precos?.[tier]?.valor_brl || 0);
      }

      return {
        ...a,
        nomePlano,
        tipo,
        valor,
        tierInfo: TIERS[tier] || TIERS.starter,
      };
    });
  }, [assinaturasAtivas, produtos, pacotes, t]);

  const valorMensalEstimado = useMemo(() => {
    return resumoAssinaturas.reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [resumoAssinaturas]);

  const handleTierTempChange = (assinaturaId, tier) => {
    setNovosTiers((prev) => ({
      ...prev,
      [assinaturaId]: tier,
    }));
  };

  const handleAlterarPlano = async (assinatura) => {
    const novoTier = novosTiers[assinatura.id];
    if (!novoTier || novoTier === assinatura.tier) return;

    try {
      setAlterandoPlanoId(String(assinatura.id));
      setMensagem('');
      setTipoMensagem('');

      const slots = (TIERS[novoTier] || TIERS.starter).licencasPorProduto;

      await api.put(`/api/assinaturas/${assinatura.id}`, {
        tier: novoTier,
        slots_contratados: slots,
      });

      setMensagem(t('dashboard.account.planSuccess'));
      setTipoMensagem('sucesso');
      await carregarDados();
    } catch (error) {
      console.error('Erro ao alterar plano:', error);

      if (error.response?.status === 403) {
        setMensagem(
          t('dashboard.account.planForbidden')
        );
      } else {
        setMensagem(
          i18n.resolvedLanguage === 'en'
            ? t('dashboard.account.planError')
            : error.response?.data?.message || t('dashboard.account.planError')
        );
      }

      setTipoMensagem('erro');
    } finally {
      setAlterandoPlanoId('');
    }
  };

  if (loading) {
    return <div className="loading">{t('dashboard.account.loading')}</div>;
  }

  if (!cliente) {
    return (
      <div className="error-message">
        {t('dashboard.account.unavailable')}
      </div>
    );
  }

  return (
    <div className="cliente-page">
      <div className="cliente-card cliente-card--wide">
        <div className="cliente-header">
          <div className="cliente-title">
            <span className="cliente-icon">👤</span>
            <div>
              <h3 className="cliente-h3">{t('dashboard.account.title')}</h3>
              <div className="cliente-subtitle">{t('dashboard.account.subtitle')}</div>
            </div>
          </div>

          <div className="cliente-badges">
            {String(cliente.status) === '1' ? (
              <span className="qt-badge qt-badge-success">{t('dashboard.account.active')}</span>
            ) : (
              <span className="qt-badge qt-badge-danger">{t('dashboard.account.inactive')}</span>
            )}

            {String(cliente.isento) === '1' ? (
              <span className="qt-badge qt-badge-info">{t('dashboard.account.exempt')}</span>
            ) : (
              <span className="qt-badge qt-badge-neutral">{t('dashboard.account.normalBilling')}</span>
            )}
          </div>
        </div>

        {mensagem && (
          <div
            className={
              tipoMensagem === 'erro'
                ? 'alert-box alert-error'
                : 'alert-box alert-success'
            }
          >
            {mensagem}
          </div>
        )}

        <div className="cliente-help-box">
          <strong>{t('dashboard.account.helpTitle')}</strong> {t('dashboard.account.help')}
        </div>

        <div className="cliente-grid">
          <div className="cliente-form-group">
            <label>{t('dashboard.account.name')}</label>
            <input type="text" value={cliente.nome || ''} disabled />
          </div>

          <div className="cliente-form-group">
            <label>CPF</label>
            <input type="text" value={cliente.cpf || ''} disabled />
          </div>

          <div className="cliente-form-group">
            <label>{t('dashboard.account.email')}</label>
            <input
              type="email"
              value={cliente.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="seuemail@exemplo.com"
            />
          </div>

          <div className="cliente-form-group">
            <label>{t('dashboard.account.phone')}</label>
            <input
              type="text"
              value={cliente.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(51) 99999-9999"
            />
          </div>

          <div className="cliente-form-group">
            <label>{t('dashboard.account.dueDay')}</label>
            <input type="text" value={cliente.dia_vencimento || '—'} disabled />
          </div>

          <div className="cliente-form-group">
            <label>{t('dashboard.account.monthlyEstimate')}</label>
            <input
              type="text"
              value={formatCurrency(valorMensalEstimado)}
              disabled
            />
          </div>
        </div>

        <div className="cliente-actions">
          <button
            type="button"
            className="btn-primary qt-btn qt-btn-primary"
            onClick={handleSalvar}
            disabled={salvando}
          >
            {salvando ? t('dashboard.account.saving') : t('dashboard.account.save')}
          </button>
        </div>
      </div>

      <div className="cliente-card cliente-card--wide" style={{ marginTop: 18 }}>
        <div className="cliente-header">
          <div className="cliente-title">
            <span className="cliente-icon">🧾</span>
            <div>
              <h3 className="cliente-h3">{t('dashboard.account.subscriptions')}</h3>
              <div className="cliente-subtitle">{t('dashboard.account.subscriptionsHelp')}</div>
            </div>
          </div>
        </div>

        {resumoAssinaturas.length === 0 ? (
          <div className="cliente-empty-box">
            {t('dashboard.account.noSubscriptions')}
          </div>
        ) : (
          <div className="cliente-subscriptions-grid">
            {resumoAssinaturas.map((assinatura) => {
              const tierAtual = String(assinatura.tier || 'starter');
              const novoTier = novosTiers[assinatura.id] || tierAtual;
              const whatsLink = buildWhatsAppPlanLink(t('dashboard.account.whatsApp', {
                customer: cliente.nome || t('dashboard.customer'),
                subscription: assinatura.nomePlano || t('dashboard.account.subscription'),
                tier: novoTier || '—',
              }));

              return (
                <div key={assinatura.id} className="subscription-card">
                  <div className="subscription-card-top">
                    <div>
                      <div className="subscription-type">
                        {assinatura.tipo === 'pacote' ? t('dashboard.account.package') : t('dashboard.account.individual')}
                      </div>
                      <div className="subscription-name">{assinatura.nomePlano}</div>
                    </div>
                    <span className={Number(assinatura.status) === 1 ? 'badge badge-ativo' : 'badge badge-inativo'}>
                      {Number(assinatura.status) === 1 ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>

                  <div className="subscription-line">
                    <span>{t('dashboard.account.currentTier')}</span>
                    <strong>{(TIERS[tierAtual] || TIERS.starter).label}</strong>
                  </div>

                  <div className="subscription-line">
                    <span>{t('dashboard.account.licensesPerProduct')}</span>
                    <strong>{assinatura.slots_contratados}</strong>
                  </div>

                  <div className="subscription-line">
                    <span>{t('dashboard.account.monthlyValue')}</span>
                    <strong>
                      {formatCurrency(assinatura.valor)}
                    </strong>
                  </div>

                  <div className="subscription-plan-change">
                    <label>{t('dashboard.account.changeQuestion')}</label>
                    <select
                      value={novoTier}
                      onChange={(e) => handleTierTempChange(assinatura.id, e.target.value)}
                    >
                      {Object.entries(TIERS).map(([key, item]) => (
                        <option key={key} value={key}>
                          {t('dashboard.account.tierOption', {
                            tier: t(`plans.tiers.${key}.name`),
                            count: item.licencasPorProduto,
                          })}
                        </option>
                      ))}
                    </select>

                    <div className="subscription-actions">
                      <button
                        type="button"
                        className="btn-primary qt-btn qt-btn-primary"
                        disabled={alterandoPlanoId === String(assinatura.id) || novoTier === tierAtual}
                        onClick={() => handleAlterarPlano(assinatura)}
                      >
                        {alterandoPlanoId === String(assinatura.id)
                          ? t('dashboard.account.changing')
                          : t('dashboard.account.change')}
                      </button>

                      <a
                        className="btn-outline-link qt-btn qt-btn-secondary"
                        href={whatsLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('dashboard.account.requestSupport')}
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
